import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Card,
  CardMedia,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Brightness7 as BrightnessIcon,
  FilterCenterFocus as CenterIcon,
  Contrast as ContrastIcon,
  Image as ImageIcon,
  Visibility as FrameShapeIcon,
  Explore as FrameDirectionIcon
} from '@mui/icons-material';
export default function ImageQualityModal({ open, onClose, image }) {

  if (!image) return null;

  const hasAnalysis = image.brightness_score !== null && image.brightness_score !== undefined;

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return <CheckIcon />;
    if (score >= 60) return <WarningIcon />;
    return <CancelIcon />;
  };

  const parseLabels = (labelsString) => {
    try {
      return JSON.parse(labelsString || '[]');
    } catch (e) {
      return [];
    }
  };

  const parseRecommendations = (recsString) => {
    try {
      return JSON.parse(recsString || '[]');
    } catch (e) {
      return [];
    }
  };

  const labels = parseLabels(image.detected_labels);
  const recommendations = parseRecommendations(image.recommendations);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ImageIcon color="primary" />
          <Typography variant="h6" component="div">
            Quality Analysis Results
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {image.item_code} - {image.image_type}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Image Preview */}
        <Card sx={{ mb: 3 }}>
          <CardMedia
            component="img"
            image={image.s3_url}
            alt={`${image.item_code} - ${image.image_type}`}
            sx={{ maxHeight: 400, objectFit: 'contain', backgroundColor: '#f5f5f5' }}
          />
        </Card>

        {/* Overall Status */}
        {!hasAnalysis && image.analysis_status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Analysis queued</strong> — Quality analysis and frame shape detection will run tonight during the nightly batch.
          </Alert>
        )}
        {!hasAnalysis && image.analysis_status !== 'pending' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This image has not been analyzed yet. Results will be available after the nightly batch.
          </Alert>
        )}
        
        {hasAnalysis && (
          <>
            {/* <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Overall Assessment
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {image.social_media_ready ? (
                  <Chip
                    icon={<CheckIcon />}
                    label="Social Media Ready"
                    color="success"
                    size="large"
                  />
                ) : (
                  <Chip
                    icon={<WarningIcon />}
                    label="Not Social Media Ready"
                    color="warning"
                    size="large"
                  />
                )}
                {image.overall_score !== null && (
                  <Chip
                    label={`Overall Score: ${parseFloat(image.overall_score).toFixed(1)}/100`}
                    color={getScoreColor(image.overall_score)}
                    size="large"
                  />
                )}
              </Box>
              {image.readiness_message && (
                <Typography variant="body2" color="text.secondary">
                  {image.readiness_message}
                </Typography>
              )}
            </Box> */}

            {/* <Divider sx={{ my: 2 }} /> */}
          </>
        )}



        {/* Frame Shape — only shown for front/external images (sort_order 1) */}
        {hasAnalysis && ['external', 'front'].includes(image.image_type) && image.sort_order === 1 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FrameShapeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Frame Shape
            </Typography>
            {(() => {
              const shape = image.frame_shape;
              const confidence = image.frame_shape_confidence;
              const status = image.frame_shape_status;

              if (shape) {
                return (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={shape}
                      color="primary"
                      size="medium"
                      sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}
                    />
                    {confidence !== null && confidence !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        Confidence: {parseFloat(confidence).toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                );
              }
              if (status === 'pending') {
                return (
                  <Chip label="Classification pending" color="warning" variant="outlined" size="small" />
                );
              }
              if (status === 'failed') {
                return (
                  <Chip label="Classification failed" color="error" variant="outlined" size="small" />
                );
              }
              return (
                <Typography variant="body2" color="text.secondary">
                  Not classified
                </Typography>
              );
            })()}
          </Box>
        )}

        {/* Frame Direction */}
        {hasAnalysis && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FrameDirectionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Frame Direction
            </Typography>
            {(() => {
              const direction = image.frame_direction;
              const confidence = image.frame_direction_confidence;
              const status = image.frame_direction_status;

              if (direction) {
                return (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={direction}
                      color="secondary"
                      size="medium"
                      sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}
                    />
                    {confidence !== null && confidence !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        Confidence: {parseFloat(confidence).toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                );
              }
              if (status === 'pending') {
                return (
                  <Chip label="Classification pending" color="warning" variant="outlined" size="small" />
                );
              }
              if (status === 'failed') {
                return (
                  <Chip label="Classification failed" color="error" variant="outlined" size="small" />
                );
              }
              return (
                <Typography variant="body2" color="text.secondary">
                  Not classified
                </Typography>
              );
            })()}
          </Box>
        )}

        {/* Centering Information */}
        {hasAnalysis && (
          <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <CenterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Centering & Composition
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Is Centered:</Typography>
              <Chip
                icon={image.is_centered ? <CheckIcon /> : <CancelIcon />}
                label={image.is_centered ? 'Yes' : 'No'}
                color={image.is_centered ? 'success' : 'error'}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Appropriate Size:</Typography>
              <Chip
                icon={image.appropriate_size ? <CheckIcon /> : <WarningIcon />}
                label={image.appropriate_size ? 'Yes' : 'No'}
                color={image.appropriate_size ? 'success' : 'warning'}
                size="small"
              />
            </Grid>
          </Grid>
          {image.centering_message && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {image.centering_message}
            </Typography>
          )}
        </Box>
        )}

        {/* Detected Labels */}
        {hasAnalysis && labels.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detected Objects
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {labels.map((label, index) => (
                <Chip
                  key={index}
                  label={typeof label === 'string' ? label : label.name || label.Name}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Recommendations */}
        {hasAnalysis && recommendations.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom color="warning.main">
              Recommendations for Improvement
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {recommendations.map((rec, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {typeof rec === 'string' ? rec : rec.recommendation || JSON.stringify(rec)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Technical Details */}
        {hasAnalysis && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Analysis Date: {new Date(image.analyzed_at).toLocaleString()}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Brand Code: {image.brand_code}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
